import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { useMuiController, useMuiForm } from '@zenput/util-common';
import { useSnackbar } from 'notistack';
import type { CSSObject } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import { ConfirmDialog } from '../../shared/ConfirmDialog';
import PageLayout from '../../shared/PageLayout';
import RichTextRenderer from '../../shared/RichTextRenderer';
import { useBackbone } from '../providers/BackboneProvider';
import { DeleteFollowUpActionModal } from './DeleteFollowUpActionModal';
import { EditSavedFollowUpAction } from './EditSavedFollowUpAction';
import {
    FollowUpActionComments,
    FollowUpActionCommentsInput,
    useFollowUpActionComments
} from './FollowUpActionComments';
import { FollowUpActionInfo } from './FollowUpActionInfo';
import {
    FollowUpActionFileType,
    FollowUpActionImageType,
    FollowUpActionReplySchema,
    FollowUpActionType,
    ReplyFollowUpActionType,
    TaskItemContext
} from './FollowUpActions.types';

type ShowFollowUpActionTab = 'info' | 'comments';

const useStyles = makeStyles()((theme) => {
    const { spacing } = theme;

    return {
        commentsTabContent: {
            overflowY: 'hidden !important',
            overflowX: 'hidden !important',
            display: 'flex !important',
            flexDirection: 'column',
            height: 'calc(100vh - 40px) !important'
        } as unknown as CSSObject,
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: `${spacing(3)} ${spacing(2)}`,
            textTransform: 'none',
            backgroundColor: 'white'
        },
        notAssignedCaption: {
            backgroundColor: 'white',
            padding: `${spacing(2)} ${spacing(2)}`,
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center'
        },
        lockIcon: {
            marginBottom: spacing(1)
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        addFUAButton: {
            textTransform: 'none',
            paddingLeft: spacing(2),
            alignSelf: 'flex-start'
        }
    };
});

export enum FUAReplies {
    BASIC = 'basic',
    FORM = 'form',
    PHOTO = 'photo'
}

interface Position {
    coords: {
        lat: number;
        lon: number;
    };
}
interface ShowFollowUpActionProps {
    completeFormActions: (followUpAction: FollowUpActionType) => void;
    getDeviceLocation: (options: {
        success: (position: Position) => void;
        error: (error: Error) => void;
        complete: () => void;
    }) => void;
    initialFollowUpAction: FollowUpActionType;
    goToSource: (id: string | number, type: string) => void;
    onGoBack: () => void;
    onGoToLocationTaskPage: () => void;
}

export const ShowFollowUpAction = (props: ShowFollowUpActionProps) => {
    const {
        completeFormActions,
        getDeviceLocation,
        goToSource,
        initialFollowUpAction,
        onGoBack,
        onGoToLocationTaskPage
    } = props;

    const [followUpAction, setFollowUpAction] = useState(initialFollowUpAction);
    const [showSpinner, setShowSpinner] = useState(false);

    const {
        deleteFollowUpAction,
        formReplyEnabled,
        getS3URL,
        hasCommentsOnFollowUpActionsDF,
        isProcessing,
        onCompleteFollowUpAction,
        updateFollowUpAction,
        uploadFilesToS3
    } = useBackbone<TaskItemContext>();

    const activeUserId = $f.active_user?.get('id') as number | undefined;

    const [activeTab, setActiveTab] = useState<ShowFollowUpActionTab>('info');
    const baseCommentSubscribers = useMemo(
        () =>
            [followUpAction.created_user, followUpAction.assigned_user].filter(
                (user): user is NonNullable<typeof user> => Boolean(user)
            ),
        [followUpAction.created_user, followUpAction.assigned_user]
    );
    const commentsState = useFollowUpActionComments(
        followUpAction.id,
        hasCommentsOnFollowUpActionsDF,
        baseCommentSubscribers
    );

    const {
        assigned_user,
        data: { description, images = [], reply },
        date_completed,
        has_delete_permission,
        has_update_permission,
        location,
        reply_type,
        source_id,
        source_type
    } = followUpAction;

    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [openEditScreen, setOpenEditScreen] = useState(false);
    const [openDeleteScreen, setOpenDeleteScreen] = useState(false);

    const [openSubmissionDialog, setOpenSubmissionDialog] = useState(false);

    const [uploadedReplyImages, setUploadedReplyImages] = useState<FollowUpActionFileType[]>([]);
    const [savedReplyImages] = useState<FollowUpActionImageType[]>(reply?.images ?? []);

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty }
    } = useMuiForm<ReplyFollowUpActionType>({
        resolver: zodResolver(FollowUpActionReplySchema),
        defaultValues: { status: 'completed', images: [], note: '' }
    });

    const { field: imagesField } = useMuiController({ control, name: 'images' });
    const { field: noteField } = useMuiController({ control, name: 'note' });

    useEffect(() => {
        if (JSON.stringify(errors) != '{}') {
            enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
        }
    }, [errors]);

    useEffect(() => {
        imagesField.onChange(uploadedReplyImages);
    }, [uploadedReplyImages]);

    const toolbarMenuItems = [
        {
            title: _t('mobile_common_button_edit'),
            onClick: () => setOpenEditScreen(true),
            visible: has_update_permission,
            disabled: !formReplyEnabled && reply_type === FUAReplies.FORM
        },
        {
            title: _t('mobile_drafts_button_delete'),
            onClick: () => setOpenDeleteScreen(true),
            visible: has_delete_permission
        }
    ];

    const handleOnUploadReplyImages = (new_images: FollowUpActionFileType[]) => {
        setUploadedReplyImages((prevImages) => [...prevImages, ...new_images]);
    };

    const handleOnDeleteUploadedImage = (indexToRemove: number) => {
        setUploadedReplyImages((prevImages) => prevImages.filter((_, index) => indexToRemove !== index));
    };

    const handleSuccessfulUpdate = async (updatedFollowUpAction: FollowUpActionType) => {
        updateFollowUpAction(
            updatedFollowUpAction,
            (response: FollowUpActionType) => {
                setFollowUpAction(response);
                enqueueSnackbar(_t('mobile_mobileform_follow_up_actions_update_notification'), { variant: 'success' });
            },
            () => {
                enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
            }
        );
        setOpenEditScreen(false);
    };

    const deleteFollowUpActionSuccess = async () => {
        deleteFollowUpAction(followUpAction.id, () => {
            enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
        });
        setOpenDeleteScreen(false);
    };
    const onSubmit = handleSubmit(async (values) => {
        if (followUpAction.data.reply?.form?.id) {
            completeFormActions(followUpAction);
            return;
        }
        try {
            const imagesToSubmit = [
                ...uploadedReplyImages.map(
                    (uploadedFile) =>
                        ({
                            s3_key: uploadedFile.s3_key,
                            is_low_quality: uploadedFile.is_low_quality,
                            laplacian_variance: uploadedFile.laplacian_variance
                        }) as FollowUpActionImageType
                )
            ];

            if (reply_type === FUAReplies.PHOTO && !imagesToSubmit.length) {
                enqueueSnackbar(_t('mobile_mobileform_follow_up_actions_required_photo_error'), { variant: 'error' });
                return;
            }

            setShowSpinner(true);
            const updatedFollowUpAction = { ...followUpAction };
            updatedFollowUpAction.data = {
                description,
                images,
                reply: {
                    images: [''],
                    note: '',
                    status: 'completed'
                }
            };

            if (values.images) {
                updatedFollowUpAction.data.reply = {
                    images: imagesToSubmit,
                    note: values.note ?? '',
                    status: 'completed'
                };
            }

            getDeviceLocation({
                success: function (position) {
                    updatedFollowUpAction.data.reply!.coordinates = position.coords;
                },
                error: function (_error) {
                    return;
                },
                complete: function () {
                    updateFollowUpAction(
                        updatedFollowUpAction,
                        () => {
                            setShowSpinner(false);
                            onCompleteFollowUpAction('mobile_mobileform_follow_up_actions_completed_notification');
                        },
                        () => {
                            setShowSpinner(false);
                            enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
                        }
                    );
                }
            });
        } catch {
            setShowSpinner(false);
        }
    });

    const handleGoToSource = () => {
        if (isDirty) {
            setOpenSubmissionDialog(true);
            return;
        }

        goToSource(source_id!, source_type);
    };

    const canSubmit = !date_completed && !isProcessing && (!assigned_user?.id || assigned_user.id === activeUserId);
    const isAssignedToOther = !date_completed && assigned_user?.id && assigned_user.id !== activeUserId;

    const submitButtonText = followUpAction.data.reply?.form?.id
        ? _t('mobile_task_detials_button_goto_form')
        : _t('mobile_mobileform_follow_up_actions_complete_follow_up_action');

    if (hasCommentsOnFollowUpActionsDF) {
        return (
            <PageLayout contentClassName={activeTab === 'comments' ? classes.commentsTabContent : undefined}>
                {{
                    appBar: (
                        <AppBar
                            toolbarMenuItems={
                                !date_completed && (has_delete_permission || has_update_permission) && toolbarMenuItems
                            }
                            title={_t('mobile_mobileform_follow_up_actions')}
                            onGoBack={onGoBack}
                        />
                    ),
                    content: (
                        <>
                            {
                                <Tabs
                                    value={activeTab}
                                    onChange={(_event, value: ShowFollowUpActionTab) => setActiveTab(value)}
                                    variant="fullWidth"
                                    sx={{ backgroundColor: 'white', position: 'sticky', top: 0, zIndex: 1 }}
                                >
                                    <Tab label={_t('mobile_dashboard_tab_title_info')} value="info" />
                                    <Tab
                                        label={
                                            commentsState.comments.length > 0
                                                ? `${_t('mobile_tasks_tab_title_comments')} (${
                                                      commentsState.comments.length
                                                  })`
                                                : _t('mobile_tasks_tab_title_comments')
                                        }
                                        value="comments"
                                    />
                                </Tabs>
                            }
                            {activeTab === 'comments' && (
                                <Box
                                    sx={{
                                        overflowY: 'hidden !important',
                                        overflowX: 'hidden !important',
                                        display: 'flex !important',
                                        flexDirection: 'column',
                                        height: 'calc(100vh - 40px) !important'
                                    }}
                                >
                                    <FollowUpActionComments
                                        comments={commentsState.comments}
                                        loading={commentsState.loading || commentsState.submitting}
                                        commentPendingDelete={commentsState.commentPendingDelete}
                                        onRequestDelete={commentsState.requestDelete}
                                        onCancelDelete={commentsState.cancelDelete}
                                        onConfirmDelete={commentsState.confirmDelete}
                                    />
                                </Box>
                            )}
                            {activeTab === 'info' && (
                                <FollowUpActionInfo
                                    followUpAction={followUpAction}
                                    getS3URL={getS3URL}
                                    onGoToLocationTaskPage={onGoToLocationTaskPage}
                                    onGoToSource={handleGoToSource}
                                    uploadedReplyImages={uploadedReplyImages}
                                    savedReplyImages={savedReplyImages}
                                    onUploadReplyImages={handleOnUploadReplyImages}
                                    onDeleteUploadedImage={handleOnDeleteUploadedImage}
                                    uploadFilesToS3={uploadFilesToS3}
                                    showSpinner={showSpinner}
                                    onLoadingChange={setShowSpinner}
                                    noteField={noteField}
                                />
                            )}

                            <EditSavedFollowUpAction
                                followUpAction={followUpAction}
                                onGoBack={() => setOpenEditScreen(false)}
                                open={openEditScreen}
                                onSuccessfulUpdate={handleSuccessfulUpdate}
                            />

                            <DeleteFollowUpActionModal
                                description={_t('mobile_mobileform_follow_up_actions_forms_delete_modal_subtitle')}
                                handleCloseConfirmation={() => setOpenDeleteScreen(false)}
                                no={_t('mobile_action_sheet_cancel')}
                                onSubmit={deleteFollowUpActionSuccess}
                                showConfirmation={openDeleteScreen}
                                title={_t('mobile_mobileform_follow_up_actions_forms_delete_modal_title')}
                                yes={_t('mobile_drafts_button_delete')}
                            />

                            <ConfirmDialog
                                description={_t('mobile_mobileform_follow_up_actions_source_dialog_description')}
                                cancelLabel={_t('mobile_mobileform_follow_up_actions_submission_dialog_stay')}
                                confirmLabel={_t('mobile_mobileform_follow_up_actions_submission_dialog_open_source')}
                                onCancel={() => {
                                    setOpenSubmissionDialog(false);
                                }}
                                onClose={() => setOpenSubmissionDialog(false)}
                                onConfirm={() => {
                                    goToSource(source_id!, source_type);
                                }}
                                open={openSubmissionDialog}
                                title={_t('mobile_mobileform_follow_up_actions_submission_dialog_title')}
                            />
                            {activeTab === 'info' && (
                                <>
                                    {isAssignedToOther && (
                                        <div className={classes.notAssignedCaption}>
                                            <LockIcon className={classes.lockIcon} />
                                            <RichTextRenderer
                                                content={_t(
                                                    'mobile_mobileform_follow_up_actions_error_not_assigned_to_you',
                                                    { user: assigned_user?.display_name }
                                                )}
                                            />
                                        </div>
                                    )}
                                    {isProcessing && !date_completed && (
                                        <div className={classes.notAssignedCaption}>
                                            <Typography>{_t('mobile_tasks_cannot_submit_processing')}</Typography>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    ),
                    bottomBar: (
                        <BottomBar elevation={activeTab === 'info' ? 1 : 0}>
                            {activeTab === 'info' && canSubmit && (
                                <div className={classes.bottomBar}>
                                    <Button
                                        className={classes.bottomButton}
                                        color="primary"
                                        disabled={showSpinner}
                                        onClick={onSubmit}
                                        variant="contained"
                                    >
                                        {submitButtonText}
                                    </Button>
                                </div>
                            )}
                            {activeTab === 'comments' && (
                                <FollowUpActionCommentsInput
                                    onSubmit={commentsState.addComment}
                                    disabled={commentsState.submitting}
                                    currentUserId={activeUserId}
                                    locationId={location?.id}
                                    savedNotifierIds={commentsState.savedNotifierIds}
                                    checkMentionPermission={commentsState.checkMentionPermission}
                                />
                            )}
                        </BottomBar>
                    )
                }}
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            {{
                appBar: (
                    <AppBar
                        toolbarMenuItems={
                            !date_completed && (has_delete_permission || has_update_permission) && toolbarMenuItems
                        }
                        title={_t('mobile_mobileform_follow_up_actions')}
                        onGoBack={onGoBack}
                    />
                ),
                content: (
                    <>
                        <FollowUpActionInfo
                            followUpAction={followUpAction}
                            getS3URL={getS3URL}
                            onGoToLocationTaskPage={onGoToLocationTaskPage}
                            onGoToSource={handleGoToSource}
                            uploadedReplyImages={uploadedReplyImages}
                            savedReplyImages={savedReplyImages}
                            onUploadReplyImages={handleOnUploadReplyImages}
                            onDeleteUploadedImage={handleOnDeleteUploadedImage}
                            uploadFilesToS3={uploadFilesToS3}
                            showSpinner={showSpinner}
                            onLoadingChange={setShowSpinner}
                            noteField={noteField}
                        />
                        <EditSavedFollowUpAction
                            followUpAction={followUpAction}
                            onGoBack={() => setOpenEditScreen(false)}
                            open={openEditScreen}
                            onSuccessfulUpdate={handleSuccessfulUpdate}
                        />

                        <DeleteFollowUpActionModal
                            description={_t('mobile_mobileform_follow_up_actions_forms_delete_modal_subtitle')}
                            handleCloseConfirmation={() => setOpenDeleteScreen(false)}
                            no={_t('mobile_action_sheet_cancel')}
                            onSubmit={deleteFollowUpActionSuccess}
                            showConfirmation={openDeleteScreen}
                            title={_t('mobile_mobileform_follow_up_actions_forms_delete_modal_title')}
                            yes={_t('mobile_drafts_button_delete')}
                        />

                        <ConfirmDialog
                            description={_t('mobile_mobileform_follow_up_actions_source_dialog_description')}
                            cancelLabel={_t('mobile_mobileform_follow_up_actions_submission_dialog_stay')}
                            confirmLabel={_t('mobile_mobileform_follow_up_actions_submission_dialog_open_source')}
                            onCancel={() => {
                                setOpenSubmissionDialog(false);
                            }}
                            onClose={() => setOpenSubmissionDialog(false)}
                            onConfirm={() => {
                                goToSource(source_id!, source_type);
                            }}
                            open={openSubmissionDialog}
                            title={_t('mobile_mobileform_follow_up_actions_submission_dialog_title')}
                        />
                        {isAssignedToOther && (
                            <div className={classes.notAssignedCaption}>
                                <LockIcon className={classes.lockIcon} />
                                <RichTextRenderer
                                    content={_t('mobile_mobileform_follow_up_actions_error_not_assigned_to_you', {
                                        user: assigned_user?.display_name
                                    })}
                                />
                            </div>
                        )}
                        {isProcessing && !date_completed && (
                            <div className={classes.notAssignedCaption}>
                                <Typography>{_t('mobile_tasks_cannot_submit_processing')}</Typography>
                            </div>
                        )}
                    </>
                ),
                bottomBar: (
                    <BottomBar>
                        {canSubmit && (
                            <div className={classes.bottomBar}>
                                <Button
                                    className={classes.bottomButton}
                                    color="primary"
                                    disabled={showSpinner}
                                    onClick={onSubmit}
                                    variant="contained"
                                >
                                    {submitButtonText}
                                </Button>
                            </div>
                        )}
                    </BottomBar>
                )
            }}
        </PageLayout>
    );
};
